/**
 * The per-SKU retry ladder — the main view of 系统健康透视.
 *
 * ── Why a table, and why the ladder is always open ──
 *
 * This replaced a pan/zoom graph canvas. The production data settled it:
 * fan-out 1–4, and exactly one of 73 upstream models shared by more than one
 * downstream SKU — 49 disjoint little trees rather than a network. A canvas
 * spent its pixels on layout that revealed no shared structure, and at ~50
 * SKUs it could not fit all three columns on screen at once, so the upstream
 * column — the one thing an operator opens this page for — was off-screen by
 * default.
 *
 * A table has the property the canvas lacked: every SKU is on one line, and
 * the lines are sortable. Its rungs render INLINE and ALWAYS EXPANDED, not
 * behind a disclosure and not in a detail pane keyed to the selected row,
 * because the endpoint (`channelSlug` + `upstreamModelName`) is the whole
 * point of the rework and a click-to-reveal would hide exactly the field
 * that was missing before. The cost is bounded — ~31 rows in the default
 * scope carrying ~1.5 rungs each — so the expanded table is about 80 lines.
 *
 * ── The one contrast every row has to make legible ──
 *
 * Production, 24h, 2026-09-01, on `claude-code/claude-fable-5`:
 *
 *   9,181 requests · 7,044 tenant-final failures · 7,033 refused before any
 *   upstream was dialled · 31 upstream 5xx · 27 of those rescued
 *
 * Three true statements about three different subjects, and each column
 * belongs to exactly one of them (`types.ts`):
 *
 *   SKU grade      ← `downstreamErrorRate`  69 of 2,206 = 3.1%. A request
 *                                           refused on its own merits never
 *                                           became our problem, so it leaves
 *                                           BOTH sides of this fraction. The
 *                                           deduction is the WHITELIST
 *                                           (6,665 + 308 + 2), not the
 *                                           structural 7,033: the other 58
 *                                           were `MODEL_NOT_AVAILABLE`, ours.
 *                                           This line read 11 of 2,148 = 0.5%
 *                                           until round 7 moved the page to
 *                                           the whitelist and left the
 *                                           comment behind.
 *   supply verdict ← `supplyRateOf`         31 of 9,181 = 0.3% (5xx floor; with
 *                                           the #2240 counters, over dials).
 *   the customer   ← `errorRate`            76.7%. Real, and about ONE
 *                                           tenant's integration — it grades
 *                                           nothing here.
 *
 * The row therefore carries two chips side by side — the SKU's own grade
 * next to the supply verdict — an outcome bar whose middle segment is the
 * refused volume in neutral grey, and the client-facing rate as text beneath
 * it. It must not read "the upstream is on fire", it must not read "this SKU
 * is on fire", and it must not read "nothing is wrong". All three
 * misreadings have a cost: the first two send an operator to debug something
 * that is working, the third leaves a paying tenant failing three quarters
 * of their calls with nobody looking.
 *
 * This comment deliberately does NOT state which two chips that row renders.
 * It said 严重 · 上游正常 for several rounds; that stopped being true when
 * `legVerdict` gained its bounds, and nobody noticed because a comment has no
 * test. A rendered verdict is a live function of thresholds, of the refusal
 * whitelist, and of a sliding window — the fable-5 SKU rate sits within ~10
 * requests of the 2% warning line — so pinning one here manufactures a claim
 * that goes stale on its own. What must hold is the RULE: each chip states a
 * verdict about its own subject, and neither borrows the other's evidence.
 * `test/health-perspective-page.test.tsx` is where a specific pairing gets
 * asserted, against a fixture that cannot drift.
 *
 * `errors - serverErrors` is NOT a quantity (those two populations are
 * orthogonal — a rescued request is in the second and not the first), so
 * nothing here derives a bucket that way. There are exactly two refusal
 * subtractions on this page and they are DIFFERENT ones: `downstreamFigures`
 * (`model-nodes.ts`) deducts the whitelist, `legFailureFigures`
 * (`model-collect.ts`) deducts the structural scalar as a bound. Treating
 * them as one quantity is what made the two surfaces read 0.5% and 2.0% off
 * one response — the divergence is deliberate and each is documented where
 * it lives.
 */

import { useMemo, useState } from 'react';
import { downstreamSeverity, fallbackSeverity } from '../../components/trafficHealth.ts';
import type { TrafficSeverity } from '../../components/trafficHealth.ts';
import { useT } from '../../i18n/index.ts';
import type { I18nKey } from '../../i18n/index.ts';
import {
  Chip,
  SortableTh,
  TBody,
  THead,
  Table,
  TableEmpty,
  Td,
  Th,
  Tr,
} from '../../ui/index.ts';
import type { ChipTone, TableSort } from '../../ui/index.ts';
import { LadderRow } from './LadderRow.tsx';
import { supplyFailuresOf, supplyRateOf } from './model-nodes.ts';
import { legDisplayRate, skuSupplyVerdict, worstLeg } from './supplyVerdict.ts';
import type { SupplyVerdict } from './supplyVerdict.ts';
import { OutcomeBarCell } from './OutcomeBar.tsx';
import { RefusalBreakdown } from './RefusalBreakdown.tsx';
import {
  GRADE_CHIP_TONE,
  GRADE_LABEL_KEY,
  downstreamDetailIsPrintable,
  formatDurationMs,
  formatEndpoint,
  formatInteger,
  formatMaybeInteger,
  formatRate,
  nodeDisplayLabel,
  severityTextClass,
} from './display.ts';
import { HEALTH_GRADE_SEVERITY } from './types.ts';
import type { SkuNode, SupplyLink, UpstreamModelNode } from './types.ts';

export type ChainSortKey =
  | 'sku'
  | 'requests'
  | 'observed'
  | 'downstream'
  | 'supply'
  | 'latency'
  | 'margin';

const COLUMN_COUNT = 8;

/**
 * The supply verdict as a chip, beside the SKU's own grade.
 *
 * Computed from the SKU's OWN legs (`supplyVerdict.ts`), never from channel
 * totals: the chip that said 上游正常 over a rung failing 100% of what this
 * SKU sent it is the bug this page was rebuilt to kill. `no_traffic` and
 * `unknown` are their own words for the same reason a null leg is — neither
 * is health.
 */
const SUPPLY_CHIP_KEY: Readonly<Record<SupplyVerdict, I18nKey>> = {
  ok: 'health_perspective.table.supply_chip.ok',
  warn: 'health_perspective.table.supply_chip.warn',
  bad: 'health_perspective.table.supply_chip.bad',
  no_traffic: 'health_perspective.table.supply_chip.no_traffic',
  unknown: 'health_perspective.table.supply_chip.unknown',
};

/**
 * Tone for the aggregate figure in the supply column. It follows the LEG
 * verdict rather than the aggregate's own rate, so the column cannot be
 * painted calm while the chip beside it says the supply is broken.
 */
const VERDICT_SEVERITY: Readonly<Record<SupplyVerdict, TrafficSeverity>> = {
  ok: 'normal',
  warn: 'warning',
  bad: 'critical',
  no_traffic: 'normal',
  unknown: 'normal',
};

const SUPPLY_CHIP_TONE: Readonly<Record<SupplyVerdict, ChipTone>> = {
  ok: 'ok',
  warn: 'warn',
  bad: 'bad',
  no_traffic: 'neutral',
  unknown: 'warn',
};

interface Props {
  readonly skus: readonly SkuNode[];
  readonly links: readonly SupplyLink[];
  readonly upstreamModels: readonly UpstreamModelNode[];
  /** `PerspectiveModel.skuChannelsReported` — decides what a null leg means. */
  readonly legsReported: boolean;
  readonly selectedNodeId: string | null;
  readonly onSelectNode: (nodeId: string) => void;
}

export function ChainTable({
  skus,
  links,
  upstreamModels,
  legsReported,
  selectedNodeId,
  onSelectNode,
}: Props): JSX.Element {
  const t = useT();
  // `null` is not "unsorted" — it is the DEFAULT order, worst grade first and
  // then busiest, which is the order an operator wants before they have
  // expressed any preference. `SortableTh` renders every header neutral for
  // it, so no column claims credit for an ordering it did not produce.
  const [sort, setSort] = useState<TableSort<ChainSortKey> | null>(null);

  const modelsById = useMemo(
    () => new Map(upstreamModels.map((model) => [model.id, model] as const)),
    [upstreamModels],
  );
  const laddersBySkuId = useMemo(() => buildLadders(links), [links]);
  const rows = useMemo(() => sortSkus(skus, sort), [skus, sort]);
  // The bar scale anchors on the busiest SKU CURRENTLY IN THE TABLE, so the
  // shapes re-scale with the scope filter. That is intentional: the bar
  // compares rows to each other, and the row it is comparing against has to
  // be one the operator can see.
  const maxRequests = useMemo(
    () => skus.reduce((max, sku) => Math.max(max, sku.traffic.requests), 0),
    [skus],
  );

  function toggleSort(key: ChainSortKey): void {
    setSort((current) =>
      current === null || current.key !== key
        ? // Numeric columns open descending: an operator sorting by failures
          // is looking for the worst row, not the cleanest one.
          { key, dir: key === 'sku' ? 'asc' : 'desc' }
        : { key, dir: current.dir === 'asc' ? 'desc' : 'asc' },
    );
  }

  return (
    <Table testId="health-perspective-chain-table" className="border-0">
      <THead>
        <Tr interactive={false}>
          <SortableTh
            label={t('health_perspective.table.col.sku')}
            sortKey="sku"
            sort={sort}
            onSort={toggleSort}
            testId="health-perspective-sort-sku"
          />
          {/* Not sortable: the bar encodes requests and composition, and
              both already have their own sortable column. A third sort with
              no defined order would be a control that means nothing. */}
          <Th>{t('health_perspective.table.col.outcome')}</Th>
          <SortableTh
            label={t('health_perspective.table.col.requests')}
            sortKey="requests"
            sort={sort}
            onSort={toggleSort}
            num
            testId="health-perspective-sort-requests"
          />
          <SortableTh
            label={t('health_perspective.table.col.observed_share')}
            sortKey="observed"
            sort={sort}
            onSort={toggleSort}
            num
            testId="health-perspective-sort-observed"
          />
          <SortableTh
            label={t('health_perspective.table.col.downstream')}
            sortKey="downstream"
            sort={sort}
            onSort={toggleSort}
            num
            testId="health-perspective-sort-downstream"
          />
          <SortableTh
            label={t('health_perspective.table.col.supply')}
            sortKey="supply"
            sort={sort}
            onSort={toggleSort}
            num
            testId="health-perspective-sort-supply"
          />
          {/* Latency as DATA, not as a verdict. It is out of the SKU grade
              entirely — "slow" is a fixed global 10s, so an image or video
              SKU is ~100% slow by construction and `codex/gpt-image-2` graded
              严重 at 88.6% slow with zero failures. It is still worth seeing,
              so it gets a plain, tone-less column. */}
          <SortableTh
            label={t('health_perspective.table.col.latency')}
            sortKey="latency"
            sort={sort}
            onSort={toggleSort}
            num
            testId="health-perspective-sort-latency"
          />
          <SortableTh
            label={t('health_perspective.table.col.margin')}
            sortKey="margin"
            sort={sort}
            onSort={toggleSort}
            num
            testId="health-perspective-sort-margin"
          />
        </Tr>
      </THead>
      {rows.length === 0 ? (
        <TBody>
          <TableEmpty colSpan={COLUMN_COUNT} testId="health-perspective-table-empty">
            {t('health_perspective.table.empty.title')}
          </TableEmpty>
        </TBody>
      ) : (
        rows.map((sku) => (
          <SkuGroup
            key={sku.id}
            sku={sku}
            rungs={laddersBySkuId.get(sku.id) ?? []}
            modelsById={modelsById}
            maxRequests={maxRequests}
            legsReported={legsReported}
            selectedNodeId={selectedNodeId}
            onSelectNode={onSelectNode}
          />
        ))
      )}
    </Table>
  );
}

/**
 * One SKU and its ladder, as a `tbody`.
 *
 * The grouping element is load-bearing rather than cosmetic: sorting reorders
 * SKUs, and a rung must travel with the SKU it belongs to. A flat row list
 * would need every sort comparator to know about parentage.
 */
function SkuGroup({
  sku,
  rungs,
  modelsById,
  maxRequests,
  legsReported,
  selectedNodeId,
  onSelectNode,
}: {
  readonly sku: SkuNode;
  readonly rungs: readonly SupplyLink[];
  readonly modelsById: ReadonlyMap<string, UpstreamModelNode>;
  readonly maxRequests: number;
  readonly legsReported: boolean;
  readonly selectedNodeId: string | null;
  readonly onSelectNode: (nodeId: string) => void;
}): JSX.Element {
  const t = useT();
  const traffic = sku.traffic;
  // `downstreamSeverity`, the same rule `gradeSkuTraffic` applies, so this
  // column and the grade chip beside it cannot disagree about the same number.
  // Null when the refusal counter is absent. A tone here would be a claim
  // computed from a substituted zero, which is the whole point of H1: on a
  // pre-counter gateway this cell used to render the raw error rate in red
  // as though it were the downstream verdict.
  const downstreamKnown =
    traffic.downstreamErrorRate !== null && traffic.attributableRequests !== null;
  const downstreamTone = downstreamKnown
    ? downstreamSeverity(traffic.downstreamErrorRate ?? 0, traffic.attributableRequests ?? 0)
    : null;
  // From this SKU's own legs. A channel-level rate cannot answer this — see
  // `supplyVerdict.ts` for the production case where it answered backwards.
  const supplyVerdict = skuSupplyVerdict(rungs, legsReported);
  const worst = worstLeg(rungs);
  const fired = traffic.fallbackRate > 0;

  return (
    <TBody className="border-b border-line [&:last-child]:border-b-0">
      <Tr
        className={selectedNodeId === sku.id ? 'bg-active' : undefined}
        data-testid={`health-perspective-row-${sku.id}`}
      >
        <Td emphasis>
          <div className="flex min-w-0 flex-col gap-1">
            <button
              type="button"
              onClick={() => onSelectNode(sku.id)}
              className="truncate text-left text-dense font-medium text-paper transition-colors duration-120 hover:text-signal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40"
            >
              {nodeDisplayLabel(sku, t)}
            </button>
            <div className="flex flex-wrap items-center gap-1.5">
              {/* The SKU's own verdict: what the customer finally saw. */}
              <Chip tone={GRADE_CHIP_TONE[sku.grade]} testId={`health-perspective-grade-${sku.id}`}>
                {t(GRADE_LABEL_KEY[sku.grade])}
              </Chip>
              {/* And, immediately beside it, whether supply had anything to
                  do with that. The pair IS the finding: 严重 · 上游正常 says
                  "real failures, not our pool" in two words. */}
              <Chip
                tone={SUPPLY_CHIP_TONE[supplyVerdict]}
                testId={`health-perspective-supply-chip-${sku.id}`}
              >
                {t(SUPPLY_CHIP_KEY[supplyVerdict])}
              </Chip>
              {/* The denominator rides with the verdict, always: the shared
                  error rule's critical arm has no minimum-sample floor, so
                  one failure in three requests grades 严重. That is the
                  gateway's rule and is not softened here — which is exactly
                  why the count has to be visible beside the word. */}
              <span className="font-mono text-micro text-paper-low">
                {t('health_perspective.table.denominator', {
                  errors: formatMaybeInteger(traffic.attributableErrors),
                  requests: formatMaybeInteger(traffic.attributableRequests),
                })}
              </span>
              {fired ? (
                <Chip
                  tone={fallbackSeverity(traffic.fallbackRate) === 'critical' ? 'bad' : 'warn'}
                  testId={`health-perspective-fired-${sku.id}`}
                >
                  {t('health_perspective.node.fallback_fired')}
                </Chip>
              ) : null}
            </div>
          </div>
        </Td>
        <Td>
          <div className="flex min-w-0 flex-col gap-1">
            <OutcomeBarCell
              traffic={traffic}
              maxRequests={maxRequests}
              testId={`health-perspective-bar-${sku.id}`}
            />
            {/* On a refusal-heavy row the codes ARE the story: which of them
                left our rate, and which stayed in it. Capped so a long tail
                cannot push a table row off the screen; the inspector has the
                full list. */}
            {(traffic.refusedBeforeUpstream ?? 0) > 0 ? (
              <RefusalBreakdown
                groups={traffic.refusedByCode}
                testId={`health-perspective-refusals-${sku.id}`}
                limit={3}
              />
            ) : null}
          </div>
        </Td>
        <Td num>{formatInteger(traffic.requests)}</Td>
        <Td num>{formatRate(traffic.observedShare)}</Td>
        {/* The downstream verdict, on the requests we took responsibility
            for. A request the gate refused never became our problem, so it
            leaves BOTH sides of this fraction — see `downstreamErrorRate` in
            `types.ts`. The tenant's own 76.7% is in the outcome cell, where
            it is a fact about a customer rather than a verdict about us. */}
        <Td num data-testid={`health-perspective-downstream-${sku.id}`}>
          <div className={downstreamTone === null ? 'text-paper-low' : severityTextClass(downstreamTone)}>
            {formatRate(traffic.downstreamErrorRate)}
          </div>
          <div className="text-micro text-paper-low">
            {/* Two different unknowns, one caveat — see
                `downstreamDetailIsPrintable`, which the inspector shares so
                the two surfaces cannot hedge differently about one node. */}
            {downstreamDetailIsPrintable(traffic)
              ? t('health_perspective.table.cell.downstream_detail', {
                  errors: formatMaybeInteger(traffic.attributableErrors),
                  requests: formatMaybeInteger(traffic.attributableRequests),
                })
              : t('health_perspective.refusal.unknown_short')}
          </div>
        </Td>
        {/* Supply: a different population, and the fallback count is quoted
            against it rather than against requests, because a fallback only
            ever had the chance to catch a request that reached an upstream. */}
        <Td num data-testid={`health-perspective-supply-${sku.id}`}>
          {/* The #2240 figure where the gateway reports it — 5xx plus the
              attempt failures the ladder moved past plus upstream
              401/402/403/429, over dials — else the 5xx floor. */}
          <div className={severityTextClass(VERDICT_SEVERITY[supplyVerdict])}>
            {formatRate(supplyRateOf(traffic))}
          </div>
          <div className="text-micro text-paper-low">
            {t('health_perspective.table.cell.supply_detail', {
              errors: formatInteger(supplyFailuresOf(traffic)),
              fallbacks: formatInteger(traffic.fallbacks),
            })}
          </div>
          {/* The aggregate above is over ALL of this SKU's requests, so a
              dead leg can hide inside it — gpt-image-2 ran 0% here while one
              leg failed everything. Naming the worst leg is what reconciles
              this cell with the chip and with the rungs below. */}
          {worst?.measured === undefined ||
          worst.measured === null ||
          legDisplayRate(worst.measured) === null ? null : (
            <div className="text-micro text-paper-low" data-testid={`health-perspective-worst-leg-${sku.id}`}>
              {/* The NET rate. Quoting the raw one here marked a SKU 上游故障
                  off a leg whose real failure count was 1. */}
              {t('health_perspective.table.cell.worst_leg', {
                // Supplier-first, same as the rung it points at.
                channel: formatEndpoint(worst, t('health_perspective.table.endpoint_unknown')),
                rate: formatRate(legDisplayRate(worst.measured)),
              })}
            </div>
          )}
        </Td>
        <Td num className="text-paper-low" data-testid={`health-perspective-latency-${sku.id}`}>
          <div>{formatDurationMs(traffic.avgFirstByteMs)}</div>
          <div className="text-micro">
            {t('health_perspective.table.cell.slow_share', {
              rate: formatRate(traffic.slowFirstByteRate),
            })}
          </div>
        </Td>
        <Td num data-testid={`health-perspective-margin-${sku.id}`}>
          {/* Never 0% for an unsettled window: the ledger settles behind live
              traffic, and a zero here would claim we sold at cost. */}
          {sku.money.settled ? (
            formatRate(sku.money.marginPct)
          ) : (
            <span className="text-paper-low">{t('health_perspective.summary.unsettled')}</span>
          )}
        </Td>
      </Tr>
      {rungs.length === 0 ? (
        <Tr interactive={false}>
          <Td colSpan={COLUMN_COUNT} className="pl-10 text-paper-low">
            {t('health_perspective.inspector.chain.empty')}
          </Td>
        </Tr>
      ) : (
        rungs.map((link) => (
          <LadderRow
            key={link.id}
            link={link}
            model={modelsById.get(link.toId) ?? null}
            columnCount={COLUMN_COUNT}
            legsReported={legsReported}
            selectedNodeId={selectedNodeId}
            onSelectNode={onSelectNode}
          />
        ))
      )}
    </TBody>
  );
}

/**
 * SKU id → its ladder, in the order the selector would attempt it: priority
 * ascending (lower goes first), then weight descending. `types.ts` says the
 * model already emits `links` in that order; re-sorting here is a cheap
 * guarantee that a future producer change cannot silently reorder a ladder
 * an operator reads top-down.
 */
function buildLadders(links: readonly SupplyLink[]): ReadonlyMap<string, readonly SupplyLink[]> {
  const out = new Map<string, SupplyLink[]>();
  for (const link of links) {
    const bucket = out.get(link.fromId);
    if (bucket === undefined) out.set(link.fromId, [link]);
    else bucket.push(link);
  }
  for (const bucket of out.values()) {
    bucket.sort((a, b) => (a.priority !== b.priority ? a.priority - b.priority : b.weight - a.weight));
  }
  return out;
}

/**
 * Sort value for one column.
 *
 * An unsettled or unrated margin has no number to compare, and inventing one
 * would put it in an arbitrary place among real margins. It sorts to the far
 * end instead — last under the descending default, which is where a row with
 * nothing to say belongs.
 */
function sortValue(sku: SkuNode, key: ChainSortKey): number {
  if (key === 'requests') return sku.traffic.requests;
  if (key === 'observed') return sku.traffic.observedShare;
  if (key === 'downstream') return sku.traffic.downstreamErrorRate ?? Number.NEGATIVE_INFINITY;
  if (key === 'supply') return supplyRateOf(sku.traffic);
  if (key === 'latency') return sku.traffic.avgFirstByteMs ?? Number.NEGATIVE_INFINITY;
  if (!sku.money.settled || sku.money.marginPct === null) return Number.NEGATIVE_INFINITY;
  return sku.money.marginPct;
}

/** The default order: worst grade first, then busiest. */
function compareDefault(a: SkuNode, b: SkuNode): number {
  const bySeverity = HEALTH_GRADE_SEVERITY[b.grade] - HEALTH_GRADE_SEVERITY[a.grade];
  if (bySeverity !== 0) return bySeverity;
  return b.traffic.requests - a.traffic.requests;
}

/** New array every time; the input is never mutated. */
function sortSkus(
  skus: readonly SkuNode[],
  sort: TableSort<ChainSortKey> | null,
): readonly SkuNode[] {
  if (sort === null) return [...skus].sort(compareDefault);
  const factor = sort.dir === 'asc' ? 1 : -1;
  if (sort.key === 'sku') {
    return [...skus].sort((a, b) => factor * a.label.localeCompare(b.label));
  }
  return [...skus].sort((a, b) => {
    const delta = sortValue(a, sort.key) - sortValue(b, sort.key);
    // A stable tiebreak keeps rows from shuffling between renders when a
    // whole column is zero, which is the common case on a short window.
    if (delta === 0 || Number.isNaN(delta)) return compareDefault(a, b);
    return factor * delta;
  });
}
